import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Plus, Pencil, Trash2, Eye, FileText, Power, PowerOff, UploadCloud, Loader2, Save } from "lucide-react";


export default function AdminBlogList() {
  const [posts, setPosts] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [newPillar, setNewPillar] = useState("");
  const [pillarError, setPillarError] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", excerpt: "", seo_subtitle: "", seo_pillar: "", tags: "", cover_image: "", display_order: 1 });
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [orderDrafts, setOrderDrafts] = useState({});
  const [savingOrderId, setSavingOrderId] = useState("");

  const loadPillars = () => api.get("/blog/admin/pillars").then((r) => setPillars(r.data || [])).catch(() => setPillars([]));

  const load = () => api.get("/blog/admin/all").then((r) => {
    setPosts(r.data);
    const nextDrafts = {};
    (r.data || []).forEach((p) => { nextDrafts[p.post_id] = p.display_order ?? 100; });
    setOrderDrafts(nextDrafts);
    // Pré-remplit l'ordre d'affichage du nouvel article = nombre d'articles + 1
    // (ne touche pas aux autres champs en cours de saisie).
    const suggestedOrder = (r.data?.length || 0) + 1;
    setForm((prev) => ({ ...prev, display_order: suggestedOrder }));
  });

  useEffect(() => { load(); loadPillars(); }, []);

  const createPillar = async (e) => {
    e.preventDefault();
    const name = newPillar.trim();
    if (!name) return;
    setPillarError("");
    try {
      await api.post("/blog/admin/pillars", { name, is_active: true });
      setNewPillar("");
      await loadPillars();
    } catch (err) {
      setPillarError(err?.response?.data?.detail || "Impossible de créer ce pilier SEO.");
    }
  };

  const deletePillar = async (pillar) => {
    if (!window.confirm(`Supprimer le pilier SEO “${pillar.name}” ?`)) return;
    setPillarError("");
    try {
      await api.delete(`/blog/admin/pillars/${pillar.pillar_id}`);
      await loadPillars();
    } catch (err) {
      setPillarError(err?.response?.data?.detail || "Impossible de supprimer ce pilier SEO.");
    }
  };

  const uploadCoverImage = async (file) => {
    if (!file) return;
    setUploadError("");

    if (!file.type?.startsWith("image/")) {
      setUploadError("Merci de sélectionner une image valide (JPG, PNG ou WebP).");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("context", "blog");
    formData.append("seo_slug", "");
    formData.append("seo_title", form.title || "");

    try {
      setUploadingCover(true);
      const { data } = await api.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, cover_image: data.url || data.secure_url || "" }));
    } catch (err) {
      const message = err?.response?.data?.detail || "Impossible d’uploader l’image pour le moment.";
      setUploadError(message);
    } finally {
      setUploadingCover(false);
    }
  };


  const create = async (e) => {
    e.preventDefault();
    const body = {
      title: form.title,
      excerpt: form.excerpt,
      seo_subtitle: form.seo_subtitle,
      seo_pillar: form.seo_pillar,
      cover_image: form.cover_image,
      display_order: Number(form.display_order || 100),
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      blocks: [],
      is_published: false,
    };
    const { data } = await api.post("/blog/posts", body);
    setCreating(false);
    setForm({ title: "", excerpt: "", seo_subtitle: "", seo_pillar: "", tags: "", cover_image: "", display_order: (posts.length || 0) + 1 });
    load();
    // jump to editor immediately
    window.location.href = `/admin/blog/${data.post_id}`;
  };

  const togglePublish = async (p) => {
    await api.patch(`/blog/posts/${p.post_id}`, { is_published: !p.is_published });
    load();
  };

  const saveOrder = async (p) => {
    const value = Number(orderDrafts[p.post_id] ?? p.display_order ?? 100);
    setSavingOrderId(p.post_id);
    try {
      await api.patch(`/blog/posts/${p.post_id}`, { display_order: Number.isFinite(value) ? value : 100 });
      await load();
    } finally {
      setSavingOrderId("");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/blog/posts/${id}`);
    await load();
    window.location.reload();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12" data-testid="admin-blog-list-page">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="label">Admin</p>
          <h1 className="font-heading text-3xl font-black flex items-center gap-2"><FileText className="w-7 h-7 text-brand-green" /> Blog</h1>
        </div>
        {!creating && <button onClick={() => setCreating(true)} className="btn-primary" data-testid="admin-create-blog-btn"><Plus className="w-4 h-4" /> Nouvel article</button>}
      </div>

      <div className="card p-5 mb-6 border border-emerald-100 bg-white/90" data-testid="admin-blog-pillars-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <p className="label">Organisation SEO</p>
            <h2 className="font-heading text-xl font-bold">Piliers SEO du blog</h2>
            <p className="text-sm text-gray-600 mt-1">Ajoute ici tes catégories piliers sans repasser par le code. Elles seront disponibles dans chaque article et sur le filtre public du blog.</p>
          </div>
        </div>
        <form onSubmit={createPillar} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 mb-4">
          <input className="input" value={newPillar} onChange={(e) => setNewPillar(e.target.value)} placeholder="Nouveau pilier SEO (ex : Éducation financière intérieure)" data-testid="blog-pillar-new-input" />
          <button className="btn-primary" type="submit" data-testid="blog-pillar-new-submit"><Plus className="w-4 h-4" /> Ajouter le pilier</button>
        </form>
        {pillarError && <p className="text-sm text-red-600 mb-3">{pillarError}</p>}
        <div className="flex flex-wrap gap-2">
          {pillars.length === 0 ? (
            <span className="text-sm text-gray-500">Aucun pilier SEO configuré.</span>
          ) : pillars.map((pillar) => (
            <span key={pillar.pillar_id || pillar.name} className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1.5 text-sm text-emerald-800">
              {pillar.name}
              <span className="text-xs text-emerald-600">{pillar.count || 0}</span>
              {(pillar.count || 0) === 0 && (
                <button type="button" onClick={() => deletePillar(pillar)} className="text-red-500 hover:text-red-700" title="Supprimer ce pilier" data-testid={`blog-pillar-delete-${pillar.name}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      </div>

      {creating && (
        <form onSubmit={create} className="card p-6 mb-6 space-y-3" data-testid="admin-blog-create-form">
          <h2 className="font-heading text-lg font-bold">Créer un article</h2>
          <input className="input" required placeholder="Titre de l'article" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-title-input" />
          <input className="input" placeholder="Sous-titre SEO / angle problème (ex : Programme pour sortir des blocages émotionnels)" value={form.seo_subtitle} onChange={(e) => setForm({ ...form, seo_subtitle: e.target.value })} data-testid="blog-seo-subtitle-input" />
          <select className="input" value={form.seo_pillar} onChange={(e) => setForm({ ...form, seo_pillar: e.target.value })} data-testid="blog-seo-pillar-input">
            <option value="">Pilier SEO de l'article</option>
            {pillars.map((pillar) => <option key={pillar.pillar_id || pillar.name} value={pillar.name}>{pillar.name}</option>)}
          </select>
          <textarea className="input" rows={2} placeholder="Résumé / extrait (apparaît dans la liste et le SEO)" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="blog-excerpt-input" />
          <input className="input" placeholder="Tags séparés par des virgules (ex: stress, mindset)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="blog-tags-input" />
          <div>
            <label className="label">Ordre d'affichage</label>
            <input className="input" type="number" min="0" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} data-testid="blog-order-input" />
            <p className="text-xs text-gray-500 mt-1">Plus le chiffre est petit, plus l’article apparaît haut dans la page blog.</p>
          </div>
          <div className="space-y-2">
            <label className="label">Image de couverture</label>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-center">
              <input className="input" placeholder="URL de l'image de couverture" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="blog-cover-input" />
              <label className="btn-ghost cursor-pointer justify-center">
                {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                {uploadingCover ? "Upload..." : "Uploader"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => uploadCoverImage(e.target.files?.[0])}
                  disabled={uploadingCover}
                  data-testid="blog-cover-upload"
                />
              </label>
            </div>
            {form.cover_image && (
              <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-3">
                <img src={form.cover_image} alt="Aperçu couverture" className="w-24 h-16 object-contain rounded-xl border border-white shadow-sm" />
                <p className="text-xs text-emerald-700 break-all">Image hébergée durablement : {form.cover_image}</p>
              </div>
            )}
            {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
          </div>
          <p className="text-xs text-gray-500">Tu pourras éditer le contenu (blocs) et la SEO juste après création.</p>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary" data-testid="blog-create-submit">Créer & ouvrir l'éditeur</button>
            <button type="button" onClick={() => setCreating(false)} className="btn-ghost">Annuler</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {posts.length === 0 && !creating && <p className="card p-10 text-center text-gray-500">Aucun article pour le moment. Crée ton premier.</p>}
        {posts.map((p) => (
          // Contenu pleine largeur, actions sur leur propre ligne en mobile.
          <div key={p.post_id} className="card p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4" data-testid={`admin-blog-row-${p.slug}`}>
            <div className="flex min-w-0 flex-1 items-start gap-3">
              {p.cover_image && <img src={p.cover_image} alt="" className="w-20 h-14 shrink-0 rounded object-contain" />}
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold leading-snug line-clamp-2 break-words">{p.title}</p>
                <span className={`badge mt-1 inline-flex text-xs ${p.is_published ? "" : "gray"}`}>{p.is_published ? "Publié" : "Brouillon"}</span>
                {p.seo_subtitle && <p className="mt-1 text-xs font-semibold text-brand-green line-clamp-1">{p.seo_subtitle}</p>}
                <p className="text-xs text-gray-500 line-clamp-1">/{p.slug}{p.seo_pillar ? ` · ${p.seo_pillar}` : ""}</p>
                <p className="text-xs text-gray-400">{p.view_count || 0} vues · {p.published_at ? `Publié le ${new Date(p.published_at).toLocaleDateString("fr-FR")}` : "Brouillon"}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-emerald-100 pt-3 lg:shrink-0 lg:border-t-0 lg:pt-0">
            <div className="flex items-center gap-2" title="Ordre d’affichage">
              <input
                type="number"
                min="0"
                className="input !w-24 !py-2 text-center"
                value={orderDrafts[p.post_id] ?? p.display_order ?? 100}
                onChange={(e) => setOrderDrafts((prev) => ({ ...prev, [p.post_id]: e.target.value }))}
                data-testid={`admin-blog-order-${p.slug}`}
              />
              <button
                onClick={() => saveOrder(p)}
                className="btn-ghost !px-3 !py-2"
                title="Enregistrer l’ordre"
                data-testid={`admin-blog-order-save-${p.slug}`}
              >
                {savingOrderId === p.post_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              </button>
            </div>
            <button onClick={() => togglePublish(p)} className={`p-2 ${p.is_published ? "text-brand-green" : "text-gray-400"}`} title={p.is_published ? "Dépublier" : "Publier"} data-testid={`admin-blog-publish-${p.slug}`}>
              {p.is_published ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}
            </button>
            <Link to={`/admin/blog/${p.post_id}`} className="btn-ghost" data-testid={`admin-blog-edit-${p.slug}`}><Pencil className="w-4 h-4" /> Éditer</Link>
            {p.is_published && <Link to={`/blog/${p.slug}`} className="btn-ghost" target="_blank" title="Voir l’article"><Eye className="w-4 h-4" /></Link>}
            <button onClick={() => remove(p.post_id)} className="ml-auto p-2 text-red-500 hover:text-red-700 lg:ml-0" title="Supprimer" data-testid={`admin-blog-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
